import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, CheckCircle, AlertCircle, Target } from 'lucide-react';

interface Loi25ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Loi25Modal({ isOpen, onClose }: Loi25ModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-green-500/30 shadow-2xl shadow-green-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-green-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
                    <FileText className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                      Loi 25 - Conformité Québec
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Protection des renseignements personnels et gouvernance des données
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-green-600/20 via-emerald-600/20 to-green-600/20 rounded-xl p-8 border-2 border-green-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  La Loi 25 : La Réalité au Québec
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  La <strong className="text-white">Loi 25</strong> (Loi modernisant des dispositions législatives en matière de protection des renseignements personnels) 
                  est la réalité pour toutes les organisations québécoises. 
                  Nous accompagnons nos clients pour qu'ils soient <strong className="text-green-400">conformes</strong>, pas juste sur papier, mais <strong className="text-green-400">vraiment</strong>.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-7 h-7 text-green-400" />
                    Exigences Principales
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-green-400 font-bold mb-2">Protection des Données</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Mesures de sécurité techniques</li>
                        <li>• Chiffrement des renseignements personnels</li>
                        <li>• Contrôle d'accès et authentification</li>
                        <li>• Sauvegarde et récupération</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-emerald-400 font-bold mb-2">Gouvernance</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Politiques et procédures documentées</li>
                        <li>• Désignation d'un responsable</li>
                        <li>• Registre des incidents</li>
                        <li>• Formation du personnel</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-green-400 font-bold mb-2">Transparence</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Avis de confidentialité clairs</li>
                        <li>• Consentement éclairé</li>
                        <li>• Droits d'accès et de rectification</li>
                        <li>• Communication en cas d'incident</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-emerald-400 font-bold mb-2">Conformité Continue</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Évaluations d'impact régulières</li>
                        <li>• Audits de conformité</li>
                        <li>• Mise à jour des mesures</li>
                        <li>• Documentation complète</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-7 h-7 text-emerald-400" />
                    Notre Approche BZ Inc.
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Chez BZ Inc., nous ne nous contentons pas de cocher des cases. Nous nous assurons que votre organisation 
                    est <strong className="text-white">vraiment conforme</strong> à la Loi 25, avec des mesures concrètes et documentées.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Évaluation complète</strong> de votre conformité actuelle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Plan d'action structuré</strong> pour atteindre la conformité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Implémentation des mesures</strong> de protection et gouvernance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Documentation professionnelle</strong> de tous les processus</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Accompagnement continu</strong> pour maintenir la conformité</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <AlertCircle className="w-7 h-7 text-yellow-400" />
                    Pourquoi C'est Important
                  </h4>
                  <p className="text-gray-300">
                    La Loi 25 n'est <strong className="text-white">pas optionnelle</strong>. C'est la loi au Québec. 
                    Les organisations doivent protéger les renseignements personnels. Nous accompagnons nos clients 
                    pour qu'ils soient conformes, pas juste sur papier, mais avec des mesures réelles et efficaces.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-green-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 font-semibold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

